import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetBrandingSettingsQuery,
  useUpdateBrandingSettingsMutation,
  useUploadBotBannerMutation,
  useDeleteBotBannerMutation,
} from '../../store/api/adminApi';

const DEFAULT_ACCENT = '#34D3C0';

const PRESET_COLORS = [
  { label: 'أكوا', value: '#34D3C0' },
  { label: 'زمردي', value: '#10B981' },
  { label: 'ذهبي', value: '#D4A017' },
  { label: 'سماوي', value: '#38BDF8' },
  { label: 'مرجاني', value: '#F97066' },
  { label: 'نيلي', value: '#6366F1' },
];

function normalizeHex(value: string): string {
  const v = value.trim();
  if (/^#[0-9A-Fa-f]{3}$/.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toUpperCase();
  }
  if (/^#[0-9A-Fa-f]{6}$/.test(v)) return v.toUpperCase();
  return '';
}

export function AdminBrandingPage() {
  const { data, isLoading } = useGetBrandingSettingsQuery();
  const [updateBranding, { isLoading: isSaving }] = useUpdateBrandingSettingsMutation();
  const [uploadBanner, { isLoading: isUploadingBanner }] = useUploadBotBannerMutation();
  const [deleteBanner, { isLoading: isDeletingBanner }] = useDeleteBotBannerMutation();
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    brandName: '',
    logoUrl: '',
    explanationUrl: '',
    supportTelegram: '',
    accentColor: DEFAULT_ACCENT,
  });

  useEffect(() => {
    if (data?.data) {
      setForm({
        brandName: data.data.brandName || '',
        logoUrl: data.data.logoUrl || '',
        explanationUrl: data.data.explanationUrl || '',
        supportTelegram: data.data.supportTelegram || '',
        accentColor: data.data.accentColor || DEFAULT_ACCENT,
      });
    }
  }, [data]);

  // Live preview while editing
  useEffect(() => {
    const hex = normalizeHex(form.accentColor);
    if (!hex) return;
    const root = document.documentElement;
    root.style.setProperty('--brand-accent', hex);
    root.style.setProperty('--brand-cyan', hex);
  }, [form.accentColor]);

  const bannerUrl = data?.data?.bannerUrl || '';

  const handleBannerPick = async (file: File | null) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('الصورة يجب أن تكون JPG أو PNG أو WebP');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('حجم البانر يجب ألا يتجاوز 3 ميغابايت');
      return;
    }
    const formData = new FormData();
    formData.append('banner', file);
    try {
      await uploadBanner(formData).unwrap();
      toast.success('تم رفع بانر البوت');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل رفع البانر');
    } finally {
      if (bannerInputRef.current) bannerInputRef.current.value = '';
    }
  };

  const handleRemoveBanner = async () => {
    try {
      await deleteBanner().unwrap();
      toast.success('تم إزالة بانر البوت');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل إزالة البانر');
    }
  };

  const handleSave = async () => {
    const accent = normalizeHex(form.accentColor);
    if (form.accentColor && !accent) {
      toast.error('لون غير صالح — استخدم صيغة مثل #2EC4B6');
      return;
    }
    try {
      await updateBranding({
        brandName: form.brandName,
        logoUrl: form.logoUrl,
        explanationUrl: form.explanationUrl,
        supportTelegram: form.supportTelegram,
        accentColor: accent || DEFAULT_ACCENT,
        websiteUrl: '',
      }).unwrap();
      toast.success('تم حفظ العلامة التجارية');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل الحفظ');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ButtonSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen animate-slide-up max-w-3xl mx-auto px-4 pb-12">
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-accent mb-2">
          العلامة التجارية
        </h1>
        <p className="text-neutral-medium">
          اسم متجرك وشعارك ولون هويتك — يظهر في اللوحة والبوت والإيميل
        </p>
      </div>

      <div className="card-glass rounded-2xl p-6 md:p-8 space-y-6 border border-brand-accent/10">
        <div>
          <label className="block text-sm text-neutral-medium mb-1">اسم المتجر / البوت</label>
          <input
            value={form.brandName}
            onChange={(e) => setForm({ ...form, brandName: e.target.value })}
            placeholder="Ahmad Shop"
            className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-xl px-4 py-2.5 focus:outline-none focus:border-brand-accent/60"
          />
        </div>

        <div className="rounded-xl border border-brand-accent/15 bg-brand-primary/10 p-4 md:p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-1">
              بانر القائمة الرئيسية للبوت
            </label>
            <p className="text-xs text-neutral-medium">
              صورة تظهر أعلى قائمة البوت عند /start — مقاس أفقي 16:9 أو 3:1 يعطي أفضل نتيجة (حد أقصى 3MB)
            </p>
          </div>

          {bannerUrl ? (
            <div className="space-y-3">
              <img
                src={bannerUrl}
                alt="Bot banner preview"
                className="w-full max-h-48 object-cover rounded-xl border border-neutral-dark/30"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => bannerInputRef.current?.click()}
                  disabled={isUploadingBanner || isDeletingBanner}
                  className="btn-secondary px-4 py-2 rounded-xl text-sm"
                >
                  {isUploadingBanner && <ButtonSpinner size="sm" />}
                  استبدال البانر
                </button>
                <button
                  type="button"
                  onClick={handleRemoveBanner}
                  disabled={isUploadingBanner || isDeletingBanner}
                  className="px-4 py-2 rounded-xl text-sm border border-red-500/40 text-red-400 hover:bg-red-500/10"
                >
                  {isDeletingBanner && <ButtonSpinner size="sm" />}
                  إزالة البانر
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => bannerInputRef.current?.click()}
              disabled={isUploadingBanner}
              className="w-full border-2 border-dashed border-brand-accent/30 rounded-xl py-10 px-4 text-center hover:border-brand-accent/60 transition-colors"
            >
              {isUploadingBanner ? (
                <ButtonSpinner />
              ) : (
                <>
                  <p className="text-sm text-neutral-light mb-1">اضغط لرفع بانر البوت</p>
                  <p className="text-xs text-neutral-medium">JPG · PNG · WebP — حتى 3MB</p>
                </>
              )}
            </button>
          )}

          <input
            ref={bannerInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleBannerPick(e.target.files?.[0] || null)}
          />
        </div>

        <div>
          <label className="block text-sm text-neutral-medium mb-1">رابط الشعار (Logo URL)</label>
          <input
            value={form.logoUrl}
            onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            placeholder="https://..."
            className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-xl px-4 py-2.5 focus:outline-none focus:border-brand-accent/60"
            dir="ltr"
          />
          {form.logoUrl && (
            <img src={form.logoUrl} alt="Logo preview" className="mt-3 h-16 object-contain" />
          )}
        </div>

        <div>
          <label className="block text-sm text-neutral-medium mb-1">رابط قناة البوت</label>
          <p className="text-xs text-neutral-medium mb-2">
            لما الزبون يضغط زر «قناة البوت» يفتح هذا الرابط (قناة تيليجرام، يوتيوب، موقع…). اتركه فارغ لإخفاء الزر.
          </p>
          <input
            value={form.explanationUrl}
            onChange={(e) => setForm({ ...form, explanationUrl: e.target.value })}
            placeholder="https://t.me/your_channel أو https://youtube.com/..."
            className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-xl px-4 py-2.5 focus:outline-none focus:border-brand-accent/60"
            dir="ltr"
          />
        </div>

        <div>
          <label className="block text-sm text-neutral-medium mb-1">قناة / يوزر الدعم (Telegram)</label>
          <input
            value={form.supportTelegram}
            onChange={(e) => setForm({ ...form, supportTelegram: e.target.value })}
            placeholder="@your_support"
            className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-xl px-4 py-2.5 focus:outline-none focus:border-brand-accent/60"
            dir="ltr"
          />
        </div>

        <div>
          <label className="block text-sm text-neutral-medium mb-2">لون الهوية (Accent)</label>
          <p className="text-xs text-neutral-medium mb-3">
            يظهر فوراً في اللوحة — احفظ لتثبيته لكل الزوار
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onClick={() => setForm({ ...form, accentColor: c.value })}
                className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${
                  normalizeHex(form.accentColor) === c.value
                    ? 'border-white scale-110 shadow-lg'
                    : 'border-transparent'
                }`}
                style={{ backgroundColor: c.value }}
              />
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <input
              type="color"
              value={normalizeHex(form.accentColor) || DEFAULT_ACCENT}
              onChange={(e) => setForm({ ...form, accentColor: e.target.value.toUpperCase() })}
              className="h-11 w-full sm:w-16 cursor-pointer rounded-lg bg-transparent border border-neutral-dark/40"
            />
            <input
              value={form.accentColor}
              onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
              placeholder="#2EC4B6"
              className="flex-1 bg-brand-primary/20 border border-neutral-dark/40 rounded-xl px-4 py-2.5 font-mono text-sm focus:outline-none focus:border-brand-accent/60"
              dir="ltr"
            />
            <div
              className="h-11 flex-1 sm:flex-none sm:w-28 rounded-xl border border-neutral-dark/30 flex items-center justify-center text-sm font-heading font-semibold text-brand-primary"
              style={{ backgroundColor: normalizeHex(form.accentColor) || DEFAULT_ACCENT }}
            >
              معاينة
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn-primary px-6 py-2.5 rounded-xl font-body flex items-center gap-2 w-full sm:w-auto justify-center"
        >
          {isSaving && <ButtonSpinner size="sm" />}
          حفظ الهوية
        </button>
      </div>
    </div>
  );
}
